import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    Alert,
    CircularProgress,
    Box,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useResponsive } from '../hooks/useResponsive';
import { messageService, MessageRecipientOption } from '../services/messageService';
// [3. javító kör, F-1] lejárat-tudatos burkolók: a küldés 401-e zárral a „megmaradtak" mondatot adja
import { requestErrorMessage, saveErrorMessage } from '../utils/saveErrors';
import { useFeedback } from '../contexts/FeedbackContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../utils/formDirty';

/** A piszkos-összevetés pillanatképe: a címzett AZONOSÍTÓJA, a tárgy és a szöveg. */
interface ComposeSnapshot {
    recipientUserId: string | null;
    subject: string;
    body: string;
}

/** Válasz esetén a címzett és a tárgy előre kitöltve érkezik. */
export interface ComposePrefill {
    recipient: { userId: string; name: string };
    subject?: string;
}

interface ComposeMessageDialogProps {
    open: boolean;
    onClose: () => void;
    /** Sikeres küldés után (pl. az elküldött-lista frissítéséhez). */
    onSent?: () => void;
    prefill?: ComposePrefill;
}

/**
 * Egyéni üzenet írása — edzőnek, játékosnak és adminnak egyaránt.
 *
 * Klub-kérés (2026-09-11): az edzők és a játékosok is írhassanak, de KORLÁTOZOTTAN: edző csak
 * díjas játékosnak, játékos csak edzőnek/adminnak, és csoportos üzenetet egyikük sem. A
 * címzettlistát a szerver adja (`/messages/recipients`) — a szabály OTT él, ez az ablak csak
 * abból választat, egyet. Válasznál a címzett kívülről jön (aki nekem írt, annak mindig
 * visszaírhatok, akkor is, ha nincs a listámban).
 */
const ComposeMessageDialog: React.FC<ComposeMessageDialogProps> = ({ open, onClose, onSent, prefill }) => {
    const { isMobile } = useResponsive();
    const { showSuccess } = useFeedback();
    const [options, setOptions] = useState<MessageRecipientOption[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [recipient, setRecipient] = useState<MessageRecipientOption | null>(null);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const confirm = useConfirm();
    /**
     * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] A MEGNYITÁSKORI állapot (válasznál
     * az előre kitöltött címzett + „Re:” tárgy) az alapvonal. Lelet: `ComposeMessageDialog.tsx:105`
     * (korábbi sor) — a háttér-kattintás / Esc / „Mégse” kérdés NÉLKÜL eldobta a hosszú üzenetet.
     * `null` = zárva → soha nem piszkos.
     */
    const [baseline, setBaseline] = useState<ComposeSnapshot | null>(null);

    useEffect(() => {
        if (!open) {
            setBaseline(null);
            return;
        }
        setError('');
        setBody('');
        setSubject(prefill?.subject ?? '');
        // Válasznál a címzett adott; nem kell a listában lennie (válasz-kivétel).
        setRecipient(
            prefill
                ? { userId: prefill.recipient.userId, name: prefill.recipient.name, roleName: '', roleDisplayName: '' }
                : null,
        );
        setBaseline({
            recipientUserId: prefill?.recipient.userId ?? null,
            subject: prefill?.subject ?? '',
            body: '',
        });

        // Válasznál a címzett adott és zárolt — a lista nem kell, és egy esetleges betöltési
        // hiba félrevezető „Nem sikerült betölteni a címzetteket” sávot tenne egy kész válasz fölé.
        if (prefill) return;

        let cancelled = false;
        setOptionsLoading(true);
        messageService
            .getRecipients()
            .then((list) => { if (!cancelled) setOptions(list); })
            .catch((err) => { if (!cancelled) setError(requestErrorMessage(err, 'Nem sikerült betölteni a címzetteket.')); })
            .finally(() => { if (!cancelled) setOptionsLoading(false); });
        return () => { cancelled = true; };
    }, [open, prefill]);

    const handleSend = async () => {
        if (!recipient) { setError('Válassz címzettet.'); return; }
        if (!subject.trim()) { setError('A tárgy megadása kötelező.'); return; }
        if (!body.trim()) { setError('Az üzenet szövege nem lehet üres.'); return; }

        setSending(true);
        setError('');
        try {
            await messageService.sendDirect({ recipientUserId: recipient.userId, subject: subject.trim(), body });
            // "X részére" — a címzett nevétől függetlenül helyes magyar toldalék nélkül is.
            showSuccess(`Az üzenet elküldve ${recipient.name} részére.`);
            // Elküldve = nincs mentetlen adat: a kapu azonnal szabadra áll (a szülő bezárása előtt).
            setBaseline(null);
            onSent?.();
            onClose();
        } catch (err) {
            setError(saveErrorMessage(err, 'Nem sikerült elküldeni az üzenetet.'));
        } finally {
            setSending(false);
        }
    };

    // Válasznál a címzett nem választható át: a válasz annak megy, aki írt.
    const recipientLocked = Boolean(prefill);

    const isDirty =
        open &&
        isFormDirty<ComposeSnapshot>(baseline, {
            recipientUserId: recipient?.userId ?? null,
            subject,
            body,
        });
    // A regisztráció a munkamenet-lejáratnál is számít: csak a bejelentett űrlap adata marad meg
    // (újra-belépő ablak a Dashboard lebontása helyett) — lásd `contexts/AuthContext.tsx`.
    useUnsavedChanges(isDirty, prefill ? 'Válasz írása' : 'Üzenet írása');

    /** Háttér-kattintás, Esc és „Mégse”: piszkos üzenetnél a közös ablak kérdez. */
    const handleClose = async () => {
        if (sending) return;
        if (isDirty && !(await confirm(discardChangesConfirm('üzenet')))) return;
        onClose();
    };

    return (
        <Dialog open={open} onClose={sending ? undefined : handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
            <DialogTitle>{prefill ? 'Válasz' : 'Új üzenet'}</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {recipientLocked ? (
                        <Typography data-testid="compose-recipient-locked">
                            Címzett: <strong>{recipient?.name}</strong>
                        </Typography>
                    ) : (
                        <Autocomplete
                            options={options}
                            loading={optionsLoading}
                            value={recipient}
                            onChange={(_, value) => setRecipient(value)}
                            getOptionLabel={(o) => o.name}
                            isOptionEqualToValue={(a, b) => a.userId === b.userId}
                            groupBy={(o) => o.roleDisplayName}
                            noOptionsText={optionsLoading ? 'Betöltés…' : 'Nincs választható címzett.'}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Címzett"
                                    required
                                    inputProps={{ ...params.inputProps, 'data-testid': 'compose-recipient-input' }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {optionsLoading ? <CircularProgress size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    )}

                    <TextField
                        label="Tárgy"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        inputProps={{ maxLength: 200, 'data-testid': 'compose-subject-input' }}
                        required
                        fullWidth
                    />

                    <TextField
                        label="Üzenet"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        inputProps={{ 'data-testid': 'compose-body-input' }}
                        multiline
                        minRows={6}
                        required
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={sending}>Mégse</Button>
                <Button
                    variant="contained"
                    startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    onClick={handleSend}
                    disabled={sending}
                    data-testid="compose-send-button"
                >
                    Küldés
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ComposeMessageDialog;
